import {
  Grid,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Divider,
  Box,
  InputAdornment,
  Input,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { useContext, useEffect, useState } from "react";
import { listBusiness } from "../../../Utils/apiService";
import { AuthUser } from "../../../interfaces/Contexts";
import { AuthContext } from "../../../Helpers/Contexts/authContext";
import { enqueueSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import KenLoader from "../../../GlobalComponents/KenLoader";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { getBusinessTypes } from "../../../Utils/apiService";


const CreateBusinessDirectory = () => {
  const location = useLocation();
  const isEditMode = location.state?.isEdit || false;
  const existingBusinessData = location.state?.businessData || null;
  const businessSource = location.state?.businessSource || 'all';
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [loading, setLoading] = useState(false);

  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [hidePhone, setHidePhone] = useState(true);
  const [hideEmail, setHideEmail] = useState(true);
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [locationUrl, setLocationUrl] = useState("");
  const [description, setDescription] = useState("");

  const [logoBase64, setLogoBase64] = useState("");
  const [logoFileName, setLogoFileName] = useState("");

  const [coverBase64, setCoverBase64] = useState("");
  const [coverFileName, setCoverFileName] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const { state: { user } } = useContext(AuthContext);
  const [logoImageUrl, setLogoImageUrl] = useState(null);
  const userData = user as AuthUser;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);

  const openLogoPreview = () => {
    setLogoPreviewOpen(true);
  };

  const closeLogoPreview = () => {
    setLogoPreviewOpen(false);
  };
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isEditMode && existingBusinessData) {
      console.log("Edit mode detected, existing data:", existingBusinessData);
      setInitialLoading(true);

      setTimeout(() => {
        setBusinessName(existingBusinessData.Name || "");
        setBusinessType(existingBusinessData.Business_Type__c || "");
        setPhone(existingBusinessData.Phone || "");
        setEmail(existingBusinessData.Business_Email__c || "");
        setHidePhone(existingBusinessData.Hide_Business_Phone__c || false);
        setHideEmail(existingBusinessData.Hide_Business_Email__c || false);
        setWebsite(existingBusinessData.Website || "");
        setAddress(existingBusinessData.Business_Address__c || "");
        setLocationUrl(existingBusinessData.Location_URL__c || "");
        setDescription(existingBusinessData.Description || "");

        if (existingBusinessData.Cover_Image_Url__c) {
          setCoverImageUrl(existingBusinessData.Cover_Image_Url__c);
        }
        if (existingBusinessData.Logo_URL__c) {
          setLogoFileName("Existing logo");
          setLogoImageUrl(existingBusinessData.Logo_URL__c);
        }

        setInitialLoading(false);
      }, 500);
    }
  }, [isEditMode, existingBusinessData]);

  const handleLogoUpload = (event: any): void => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateFileSize(file, 1)) {
        enqueueSnackbar('Logo image must be less than 1MB', { variant: 'error' });
        return;
      }
      const allowedTypes: any = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validateFileType(file, allowedTypes)) {
        enqueueSnackbar('Logo must be in PNG or JPEG format', { variant: 'error' });
        return;
      }
      const imagePreview: any = URL.createObjectURL(file);
      setLogoImageUrl(imagePreview);
      setLogoFileName(file.name);

      const reader: any = new FileReader();
      reader.onloadend = () => {
        const base64String: any = reader.result as string;
        setLogoBase64(base64String.split(",")[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageUpload = (event: any): void => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateFileSize(file, 5)) {
        enqueueSnackbar('Cover image must be less than 5MB', { variant: 'error' });
        return;
      }
      const allowedTypes: any = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validateFileType(file, allowedTypes)) {
        enqueueSnackbar('Cover image must be in PNG or JPEG format', { variant: 'error' });
        return;
      }
      const imagePreview: any = URL.createObjectURL(file);
      setCoverImageUrl(imagePreview);
      setCoverFileName(file.name);
      const reader: any = new FileReader();
      reader.onloadend = () => {
        const base64String: any = reader.result as string;
        setCoverBase64(base64String.split(",")[1]);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    if (!businessName.trim()) {
      enqueueSnackbar('Business name is required', { variant: 'error' });
      return;
    }

    if (!businessType) {
      enqueueSnackbar('Business type is required', { variant: 'error' });
      return;
    }

    if (!phone.trim()) {
      enqueueSnackbar('Phone number is required', { variant: 'error' });
      return;
    }

    if (!email.trim()) {
      enqueueSnackbar('Email is required', { variant: 'error' });
      return;
    }

    if (phone && !validatePhone(phone)) {
      enqueueSnackbar('Please enter a valid phone number', { variant: 'error' });
      return;
    }

    if (email && !validateEmail(email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' });
      return;
    }

    if (website && !validateURL(website)) {
      enqueueSnackbar('Please enter a valid website URL', { variant: 'error' });
      return;
    }

    if (locationUrl && !validateMapURL(locationUrl)) {
      enqueueSnackbar('Please enter a valid map URL (e.g., https://maps.app.goo.gl/...)', { variant: 'error' });
      return;
    }

    setLoading(true);

    const data = {
      id: isEditMode ? existingBusinessData.Id : undefined,
      name: businessName,
      contactIds: [userData?.applicantId],
      ...(isEditMode && { businessId: existingBusinessData.Id }),  // Only include businessId when in edit mode
      description: description,
      businessType: businessType,
      phone: phone,
      email: email,
      hidePhone: hidePhone,
      hideEmail: hideEmail,
      website: website,
      address: address,
      locationurl: locationUrl,
      ...(logoBase64 ? {
        businessLogo: {
          fileName: logoFileName,
          fileBlob: logoBase64
        }
      } : {}),
      ...(coverBase64 ? {
        coverImage: {
          fileName: coverFileName,
          fileBlob: coverBase64
        }
      } : {})
    };

    try {
      const response = await listBusiness(data);

      if (response && !response.message) {
        enqueueSnackbar(`Business ${isEditMode ? 'updated' : 'listing submitted'} successfully!`, { variant: 'success' });
        navigate('/business directory');
      } else {
        enqueueSnackbar(response.message || `Failed to ${isEditMode ? 'update' : 'submit'} business listing`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('An error occurred. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateFileSize = (file: any, maxSizeInMB: any): boolean => {
    const fileSizeInMB = file.size / (1024 * 1024);
    return fileSizeInMB <= maxSizeInMB;
  };

  const validateFileType = (file: any, allowedTypes: any): boolean => {
    return allowedTypes.includes(file.type);
  };

  const validateEmail = (email: any): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone: any): boolean => {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(phone.replace(/\s+/g, ''));
  };

  const validateURL = (url: any): boolean => {
    if (!url.trim()) return false;
    let urlToCheck = url;
    if (!url.match(/^https?:\/\//i)) {
      urlToCheck = 'https://' + url;
    }
    if (url.startsWith('www.') && !url.match(/^https?:\/\//i)) {
      urlToCheck = 'https://' + url;
    }
    try {
      new URL(urlToCheck);
      return true;
    } catch (error) {
      return false;
    }
  };
  const validateMapURL = (url: any): boolean => {
    if (!url.trim()) return false;

    let urlToCheck = url;
    if (!url.match(/^https?:\/\//i)) {
      urlToCheck = 'https://' + url;
    }

    try {
      new URL(urlToCheck);

      const mapUrlPatterns = [
        /maps\.app\.goo\.gl\//i,
        /goo\.gl\/maps\//i,
        /google\.com\/maps/i,
        /maps\.google\.com/i,
        /maps\.apple\.com/i,
        /bing\.com\/maps/i,
        /waze\.com/i,
        /openstreetmap\.org/i,
        /here\.com/i,
        /mapquest\.com/i,
        /yandex\.com\/maps/i,
        /2gis\.com/i
      ];
      return mapUrlPatterns.some(pattern => pattern.test(urlToCheck));
    } catch (error) {
      return false;
    }
  };
  const [businessTypes, setBusinessTypes] = useState<any>(null);

  const fetchBusinessTypes = async () => {
    try {
      const response = await getBusinessTypes();
      const records = response?.data?.records || [];

      // Move "Other" to the end
      const sortedRecords = [
        ...records.filter((item: any) => item.Label !== 'Other'),
        ...records.filter((item: any) => item.Label === 'Other'),
      ];

      setBusinessTypes({ ...response, data: { ...response.data, records: sortedRecords } });
      console.log("Business Types:", sortedRecords);
    } catch (error) {
      console.error("Error fetching business types:", error);
    }
  };

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  return (
    <>
      {initialLoading && <KenLoader />}

      {/* Only show the form when not in initial loading state */}
      {!initialLoading && (
        <Box sx={{
          backgroundColor: "white",
          borderRadius: 2,
          p: { xs: 1.5, sm: 3 },
          marginBottom: "3rem",
          mx: { xs: 1, sm: 0 },
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 120px)"
        }}>
          <Typography fontWeight={500} color="#373A45" mb={2} textAlign={"left"}>
            {isEditMode ? "Edit your business" : "Please fill in the form to setup your business"}
          </Typography>

     <Input
  type="file"
  style={{ display: "none" }}
  onChange={handleCoverImageUpload}
  inputProps={{ accept: "image/*" }}
  id="upload-cover-image-input"
/>

<label htmlFor="upload-cover-image-input" style={{ width: "100%" }}>
  <Grid
    container
    justifyContent="center"
    alignItems="center"
    sx={{
      height: { xs: "160px", sm: "199px" },
      backgroundColor: "#EAEFFF",
      marginTop: "10px",
      borderRadius: "9px",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      flexShrink: 0,
      cursor: "pointer",          // <-- Important for UX
    }}
  >
    {coverImageUrl ? (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          borderRadius: "9px",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={coverImageUrl}
          alt="Cover Preview"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "9px",
          }}
        />

        {/* Overlay content */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "10px 20px",
            borderRadius: "5px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              color: "white",
              marginTop: "5px",
              wordBreak: "break-word",
            }}
          >
            {coverFileName}
          </Typography>
        </Box>
      </Box>
    ) : (
      <>
        <CloudUploadIcon fontSize="large" color="primary" />
        <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
          Upload a cover picture (optional)
        </Typography>
        <Typography sx={{ fontSize: "9px", fontWeight: 400 }}>
          Max image size: 5 MB | Min width: 1028px | Image format: PNG, JPEG
        </Typography>
      </>
    )}
  </Grid>
</label>


          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              my: 2,
              pr: 1
            }}
            className="scrollbar-cusgray"
          >
            <Grid sx={{
              margin: { xs: "20px 0", sm: "50px 100px", md: "50px 200px" },
              textAlign: "left",
              px: { xs: 2, sm: 0 }
            }}>
              {/* Upload Logo and Business Name */}
              <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Upload your logo (optional)
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {logoImageUrl ? (
                      <>
                        <Button
                          variant="contained"
                          component="label"
                          fullWidth
                          sx={{
                            height: 56,
                            backgroundColor: "#1e4d94",
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "#153a70",
                              color: "#fff",
                            }
                          }}
                        >
                          Change Logo
                          <input hidden type="file" onChange={handleLogoUpload} accept="image/png, image/jpeg" />
                        </Button>

                        <IconButton
                          onClick={openLogoPreview}
                          sx={{
                            backgroundColor: "#e0e0e0",
                            height: 56,
                            width: 56,
                            borderRadius: 1
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ justifyContent: "flex-start", height: 56 }}
                      >
                        {logoFileName || "Choose file"}
                        <input hidden type="file" onChange={handleLogoUpload} accept="image/png, image/jpeg" />
                      </Button>
                    )}
                  </Box>

                  <Typography fontSize={12} mt={1}>
                    Max image size: 1 MB | Image format: PNG, JPEG
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Name of your business <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter name"
                    required
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                  />
                </Grid>

                {/* Business Type */}
                <Grid item xs={12}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Business type <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    placeholder="Select type"
                    required
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                  >
                    {businessTypes?.data?.records?.map((record: any, index: number) => {
                      return <MenuItem key={index} value={record.Label}>
                        {record.Label}
                      </MenuItem>
                    })}
                  </TextField>
                </Grid>
              </Grid>
              <Dialog
                open={logoPreviewOpen}
                onClose={closeLogoPreview}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>Logo Preview</Typography>
                  <IconButton onClick={closeLogoPreview} edge="end">
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 2,
                      minHeight: 300,
                    }}
                  >
                    <Box
                      component="img"
                      src={logoImageUrl || ''}
                      alt="Logo Preview"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 400,
                        objectFit: 'contain',
                      }}
                      onError={(e: any) => {
                        console.error("Error loading logo image:", e);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZWVlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPkxvZ28gSW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeLogoPreview} variant="outlined">
                    Close
                  </Button>
                  <Button
                    component="label"
                    variant="contained"
                  >
                    Change Logo
                    <input hidden type="file" onChange={(e) => {
                      handleLogoUpload(e);
                      closeLogoPreview();
                    }} accept="image/png, image/jpeg" />
                  </Button>
                </DialogActions>
              </Dialog>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Phone number <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="0000 0000 00"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                    }}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[A-Za-z]/g, ''))}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hidePhone}
                        onChange={(e) => setHidePhone(e.target.checked)}
                      />
                    }
                    label="Hide from my profile"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Email ID <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Enter email ID"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideEmail}
                        onChange={(e) => setHideEmail(e.target.checked)}
                      />
                    }
                    label="Hide from my profile"
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <Typography fontSize={14} fontWeight={500} mb={1}>
                  Website
                </Typography>
                <TextField
                  fullWidth
                  placeholder="www.https://"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter business address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography fontSize={14} fontWeight={500} mb={1}>
                    Map URL
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g., https://maps.app.goo.gl/..."
                    value={locationUrl}
                    onChange={(e) => setLocationUrl(e.target.value)}
                    error={locationUrl.trim() !== "" && !validateMapURL(locationUrl)}
                  />
                </Grid>
              </Grid>

              {/* Business Description */}
              <Box mt={3}>
                <Typography
                  fontSize={{ xs: 12, sm: 14 }}
                  fontWeight={500}
                  mb={1}
                >
                  Business description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Enter business description"
                  inputProps={{ maxLength: 8000 }}
                  helperText={`${description.length} / 8000`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Box>
            </Grid>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{
              borderTop: '1px solid #EAEFFF',
              pt: 2,
              pb: 1,
              mt: 'auto',
              flexShrink: 0
            }}
          >
            <Button
              onClick={() => navigate('/business directory')}
              sx={{ color: "#5B4200", mr: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading && <KenLoader />}
              sx={{ color: "#fff" }}
            >
              {loading ? "Submitting..." : (isEditMode ? "Update business" : "Request listing")}
            </Button>
          </Box>
        </Box>
      )}

      {loading && <KenLoader />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as any}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateBusinessDirectory;
